import React, { useEffect, useState } from 'react';
import Header from '../Components/Header';
import { useParams } from "react-router-dom";
import { useNavigate } from 'react-router-dom';
import DataTable from 'react-data-table-component';
import {
    Badge,
    Button,
    Container,
    Row,
    Col,
    Card,
    CardBody,
    CardHeader,
    Spinner,
    Nav,
    NavItem,
    NavLink
} from 'reactstrap';
import { BASE_URL } from '../config';

export default function DoctorSchedule() {
    const navigate = useNavigate();
    const [doctorSchedule, setDoctorSchedule] = useState([]);
    const [validationMessages, setValidationMessages] = useState({});
    const [loading, setLoading] = useState(true);
    const [selectedTab, setSelectedTab] = useState('active');
    const { id } = useParams();

    const currentDay = new Date().getDay();
    const currentTime = new Date().getHours() * 60 + new Date().getMinutes();

    // Fetch doctor schedule data on component mount
    useEffect(() => {
        const fetchDoctorProfile = async () => {
            try {
                const response = await fetch(`${BASE_URL}SpecialistDoctorSchedule?Userid=${id}`, {
                    method: 'GET',
                    headers: {
                        'accept': 'text/plain'
                    }
                });

                const data = await response.text();

                const parsedData = JSON.parse(data);
                if (parsedData && parsedData.getSpecialistScheduleDetail) {
                    setDoctorSchedule(parsedData.getSpecialistScheduleDetail);
                } else {
                    alert(data.message);
                }
            } catch (error) {
                console.error('Error fetching doctor profile:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchDoctorProfile();
    }, [id]);

    // Filter the schedule based on selected tab
    const getFilteredSchedule = () => {
        if (selectedTab === 'active') {
            return doctorSchedule.filter(schedule => schedule.status === 'Active');
        } else if (selectedTab === 'inactive') {
            return doctorSchedule.filter(schedule => schedule.status === 'Inactive');
        }
        return doctorSchedule;
    };

    // Sort schedules by day of the week (Sunday to Saturday)
    const sortByDayOfWeek = (schedules) => {
        const daysOrder = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
        return schedules.sort((a, b) => {
            return daysOrder.indexOf(a.dayofweek) - daysOrder.indexOf(b.dayofweek);
        });
    };

    // Get sorted schedule data
    const sortedSchedules = sortByDayOfWeek(getFilteredSchedule());

    // Navigate to the Create Doctor Schedule page
    const handleRegisterClick = () => {
        navigate(`/CreateDoctorSchedule/${id}`);
    };

    // Validate fields for each schedule entry
    const validateFields = () => {
        const messages = {};
        let isValid = true;

        doctorSchedule.forEach((row, index) => {
            if (!row.doctorName || row.doctorName.trim() === '') {
                messages[`doctorName-${index}`] = 'Please fill the Doctor Name';
                isValid = false;
            }
            if (!row.startTime || row.startTime.trim() === '') {
                messages[`startTime-${index}`] = 'Please fill the Start Time';
                isValid = false;
            }
            if (!row.endTime || row.endTime.trim() === '') {
                messages[`endTime-${index}`] = 'Please fill the End Time';
                isValid = false;
            }
        });

        setValidationMessages(messages);
        return isValid;
    };

    // Handle the Edit button click
    const handleEditClick = (row) => {
        setValidationMessages({});
        if (validateFields()) {
            navigate(`/EditDoctorSchedule/${row.scheduleId}`);
        }
    };

    // Format time to AM/PM format
    const formatTime = (timeString) => {
        const [hours, minutes] = timeString.split(':');
        let hoursInt = parseInt(hours, 10);
        let period = 'AM';
        if (hoursInt >= 12) {
            period = 'PM';
            if (hoursInt > 12) {
                hoursInt -= 12;
            }
        }
        if (hoursInt === 0) {
            hoursInt = 12;
        }
        return `${hoursInt}:${minutes} ${period}`;
    };

    // Convert time string to minutes
    const timeToMinutes = (timeString) => {
        const [hours, minutes] = timeString.split(':').map(Number);
        return hours * 60 + minutes;
    };

    // Get day index from day name
    const getDayIndex = (dayName) => {
        const days = {
            Sunday: 0,
            Monday: 1,
            Tuesday: 2,
            Wednesday: 3,
            Thursday: 4,
            Friday: 5,
            Saturday: 6,
        };
        return days[dayName] || -1;
    };

    // Define the columns for the DataTable
    const columns = [
        {
            name: <span className="fw-bold">Status</span>,
            selector: row => (
                <Badge color={row.status === "Active" ? 'success' : 'danger'}>
                    {row.status || 'N/A'}
                </Badge>
            ),
            sortable: true,
        },
        {
            name: <span className="fw-bold">Start Time</span>,
            selector: row => formatTime(row.startTime) || 'N/A',
            sortable: true,
            cell: (row, index) => (
                <div>
                    {formatTime(row.startTime) || 'N/A'}
                    {validationMessages[`startTime-${index}`] && (
                        <span style={{ color: 'red', marginLeft: '10px' }}>
                            {validationMessages[`startTime-${index}`]}
                        </span>
                    )}
                </div>
            )
        },
        {
            name: <span className="fw-bold">End Time</span>,
            selector: row => formatTime(row.endTime) || 'N/A',
            sortable: true,
            cell: (row, index) => (
                <div>
                    {formatTime(row.endTime) || 'N/A'}
                    {validationMessages[`endTime-${index}`] && (
                        <span style={{ color: 'red', marginLeft: '10px' }}>
                            {validationMessages[`endTime-${index}`]}
                        </span>
                    )}
                </div>
            )
        },
        {
            name: <span className="fw-bold">Day Of Week</span>,
            selector: row => row.dayofweek || 'N/A',
            sortable: true,
        },
        // {
        //     name: <span className="fw-bold">Create Time</span>,
        //     selector: row => row.createtime || 'N/A',
        //     sortable: true,
        // },
        {
            name: <span className="fw-bold">Action</span>,
            cell: row => (
                <Button variant="primary" size="sm" className='btn_primary' onClick={() => handleEditClick(row)}>
                    Edit
                </Button>
            ),
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
        }
    ];

    // Conditional row styles based on current time
    const conditionalRowStyles = [
        {
            when: row => {
                const dayOfWeek = row.dayofweek;
                const startTime = timeToMinutes(row.startTime);
                const endTime = timeToMinutes(row.endTime);
                return currentDay === getDayIndex(dayOfWeek) && currentTime >= startTime && currentTime <= endTime;
            },
            style: {
                backgroundColor: '#ffcccb',
                animation: 'blink 1s infinite',
            },
        }
    ];

    return (
        <Container fluid className='h-custom py-1'>
            <Header />
            <Row className='d-flex justify-content-center align-items-center h-45'>
                <Col xs="12" className="mt-3">
                    <Card className='card-registration card-registration-2' style={{ borderRadius: '12px' }}>
                        <CardHeader className="text-center bg_primary text-white border-0">
                            <Row>
                                <Col lg="10" md="9" xs="6">
                                    <h5 className="fw-bold mb-1">Doctor Schedule</h5>
                                </Col>
                                <Col lg="2" md="3" xs="6">
                                    <Button className='mx-2 px-0 w-100 bg-white text_primary' onClick={handleRegisterClick}>
                                        Add New Schedule
                                    </Button>
                                </Col>
                            </Row>
                        </CardHeader>
                        {loading ? (
                            <div className="d-flex justify-content-center align-items-center" style={{ height: '200px' }}>
                                <Spinner size="lg" />
                            </div>
                        ) : (
                            <>
                                <Nav tabs className='bg_light p-1'>
                                    <NavItem>
                                        <NavLink
                                            className={selectedTab === 'active' ? 'tab_active' : ''}
                                            onClick={() => setSelectedTab('active')}
                                        >
                                            Active
                                        </NavLink>
                                    </NavItem>
                                    <NavItem>
                                        <NavLink
                                            className={selectedTab === 'inactive' ? 'tab_active' : ''}
                                            onClick={() => setSelectedTab('inactive')}
                                        >
                                            Inactive
                                        </NavLink>
                                    </NavItem>
                                    <NavItem>
                                        <NavLink
                                            className={selectedTab === 'all' ? 'tab_active' : ''}
                                            onClick={() => setSelectedTab('all')}
                                        >
                                            All
                                        </NavLink>
                                    </NavItem>

                                </Nav>
                                <CardBody className='p-2'>
                                    <DataTable
                                        columns={columns}
                                        data={sortedSchedules}
                                        pagination
                                        responsive
                                        highlightOnHover
                                        pointerOnHover
                                        conditionalRowStyles={conditionalRowStyles}
                                        noDataComponent="No schedules available"
                                    />
                                </CardBody>
                            </>
                        )}
                    </Card>
                </Col>
            </Row>
        </Container>
    );
}
